import { useRef } from 'react';
import { ListMutation, ListMutationKind } from '../utils/listMutationLog';

/**
 * Obergrenze in **Eintraegen**, nicht in Datensaetzen: ein Import mit 450
 * Kunden ist EIN Eintrag. Praktisch unerreichbar; die Grenze existiert, damit
 * ein pathologischer Zustand (nie zurueckkehrende Ladung + Dauer-Mutationen)
 * nicht in unbegrenztes Speicherwachstum und beliebig altes Clobbering laeuft.
 * Wird sie ueberschritten, faellt der Ladepfad auf das historische Verhalten
 * zurueck (Antwort verwerfen) — per Definition keine Regression.
 */
export const MAX_PENDING_MUTATIONS = 200;

/**
 * Ref-gestuetzter Container fuer das Mutations-Log einer Liste (M-2).
 *
 * Der Container besitzt bewusst **nicht** die Liste, `loading` oder `error` —
 * er ist nur das Gedaechtnis "was hat der Nutzer seit dem Start dieser Ladung
 * lokal getan?". Damit bleibt der Diff in den (muehsam stabilisierten)
 * Ladepfaden minimal.
 */
export interface ListMutationLog<T> {
  /**
   * Startmarke einer Ladung. Der Aufrufer merkt sich den Rueckgabewert und
   * uebergibt ihn spaeter an {@link ListMutationLog.pendingSince}.
   */
  mark(): number;

  /**
   * Protokolliert ein Anlegen (insert-or-replace beim Replay).
   *
   * NUR nach der Server-Bestaetigung aufrufen (nach dem `await` der
   * Firestore-Operation) und NIE innerhalb eines `setState`-Updaters:
   * React 18 StrictMode ruft Updater doppelt auf ⇒ Doppel-Eintrag (Invariante
   * I-4). Ein fehlgeschlagener Schreibvorgang darf keinen Eintrag
   * hinterlassen, der einen nicht existierenden Datensatz in jede spaetere
   * Serverantwort einspiegelt.
   */
  recordCreate(items: readonly T[]): void;

  /** Protokolliert eine Aenderung (patch-if-present beim Replay). Bedingungen wie {@link ListMutationLog.recordCreate}. */
  recordUpdate(items: readonly T[]): void;

  /** Protokolliert eine Loeschung (remove beim Replay). Bedingungen wie {@link ListMutationLog.recordCreate}. */
  recordDelete(ids: readonly string[]): void;

  /**
   * Eintraege mit `seq > since`, aufsteigend sortiert.
   * Leeres Array ⇒ die Antwort dieser Ladung ist aktuell.
   */
  pendingSince(since: number): ListMutation<T>[];

  /**
   * Nach einer autoritativen (nicht ueberholten) Antwort, die die Liste
   * GESCHRIEBEN hat: alle Eintraege sind eingebacken ⇒ Log leeren.
   *
   * Warum das ganze Log und nicht nur `seq <= since`: schreibt eine Ladung,
   * ist sie per Definition nicht ueberholt, also die juengste. Jede aeltere
   * offene Ladung ist ueberholt und wird niemals schreiben, eine juengere kann
   * es nicht geben. Zwischen `pendingSince`, `setList` und `settleApplied`
   * kann kein Eintrag dazukommen (synchroner Block, JS ist einfaedig).
   * Umgekehrt ist Nicht-Pruning unsicher: ein Eintrag, der eine spaeter
   * gestartete Ladung ueberlebt, wuerde deren frischeren Serverwert mit einer
   * veralteten lokalen Nutzlast ueberschreiben.
   */
  settleApplied(): void;

  /**
   * Nach einer autoritativen Antwort, die NICHT geschrieben hat (catch-Zweig,
   * Overflow-Fallback): Eintraege mit `seq <= since` verwerfen — sie sind in
   * jeder kuenftigen Serverantwort enthalten. Spaetere Eintraege bleiben
   * stehen, weil noch keine Antwort sie kennt.
   */
  settleUpTo(since: number): void;

  /** Vollstaendiges Zuruecksetzen (Unmount, Scope-Wechsel). */
  reset(): void;

  /** `true`, sobald das Log {@link MAX_PENDING_MUTATIONS} ueberschritten hat. */
  isOverflowed(): boolean;
}

/**
 * Legt ein ueber die Lebensdauer der Komponente stabiles Mutations-Log an.
 *
 * Das Log lebt in einem `useRef` und loest bewusst **kein** Re-Render aus:
 * es beschreibt nur, was beim naechsten Antwort-Eingang ueber die Serverbasis
 * zu legen ist. Die sichtbare Liste bleibt normaler React-State.
 *
 * @param getId Extrahiert die Identitaet eines Datensatzes. Wird hier nur
 *   durchgereicht/dokumentiert — das Log speichert Nutzlasten unveraendert,
 *   die Identitaet wertet erst `applyListMutations` aus. Der Parameter haelt
 *   den Aufrufer an, ueberall dieselbe ID-Quelle zu verwenden.
 * @returns Ein stabiles {@link ListMutationLog} (Referenz aendert sich nie,
 *   darf also gefahrlos in `useCallback`-Dependencies stehen).
 */
export function useListMutationLog<T>(getId: (item: T) => string): ListMutationLog<T> {
  const getIdRef = useRef(getId);
  getIdRef.current = getId;

  const entriesRef = useRef<ListMutation<T>[]>([]);
  const seqRef = useRef(0);
  const overflowedRef = useRef(false);
  const apiRef = useRef<ListMutationLog<T> | null>(null);

  if (apiRef.current === null) {
    const append = (kind: ListMutationKind, items: readonly T[], ids: readonly string[]): void => {
      const entry: ListMutation<T> = {
        seq: ++seqRef.current,
        kind,
        // Defensiv kopieren: der Aufrufer darf sein Array danach weiterverwenden.
        ids: [...ids],
        items: [...items],
      };
      entriesRef.current.push(entry);

      if (entriesRef.current.length > MAX_PENDING_MUTATIONS && !overflowedRef.current) {
        overflowedRef.current = true;
        // `console.warn` wird in Produktionsbuilds von `utils/logger` auf
        // `logger.warn` umgebogen; hier bewusst kein Toast und kein `error` —
        // der Merge ist fuer den Nutzer unsichtbar.
        console.warn(
          `[listMutationLog] Mutations-Log ueber ${MAX_PENDING_MUTATIONS} Eintraege — ` +
            'Ladeantworten werden bis zum naechsten Abgleich verworfen (Fallback auf das historische Verhalten).',
        );
      }
    };

    apiRef.current = {
      mark: () => seqRef.current,

      recordCreate: (items) => {
        if (items.length === 0) return;
        append('create', items, []);
      },

      recordUpdate: (items) => {
        if (items.length === 0) return;
        append('update', items, []);
      },

      recordDelete: (ids) => {
        if (ids.length === 0) return;
        append('delete', [], ids);
      },

      pendingSince: (since) =>
        entriesRef.current.filter((entry) => entry.seq > since).sort((a, b) => a.seq - b.seq),

      settleApplied: () => {
        entriesRef.current = [];
        overflowedRef.current = false;
      },

      settleUpTo: (since) => {
        entriesRef.current = entriesRef.current.filter((entry) => entry.seq > since);
        if (entriesRef.current.length <= MAX_PENDING_MUTATIONS) overflowedRef.current = false;
      },

      reset: () => {
        entriesRef.current = [];
        overflowedRef.current = false;
      },

      isOverflowed: () => overflowedRef.current,
    };
  }

  return apiRef.current;
}
